import { map, Observable, of } from "rxjs";

export interface IThumbnailConfig {
  version: string;
  thumbnails: {
    totalSpriteCount: number;
    spritesPerRow: number;
    spritesPerColumn: number;
    spriteWidth: number;
    spriteHeight: number;
    sheetURLs: string[];
  };
}

export interface IThumbnailResult {
  sheetURL: string;
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
  sheetIndex: number;
}

/**
 * Cache for already-loaded sprite sheets.
 * Key = sheet URL, Value = loaded HTMLImageElement
 */
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Returns thumbnail metadata (URL + crop info) for a given position in video.
 */
export function getThumbnailForPosition(
  config: IThumbnailConfig,
  position: number,
  duration: number
): IThumbnailResult | null {
  if (!config || !config.thumbnails) return null;
  const thumbs = config.thumbnails;
  const {
    totalSpriteCount,
    spritesPerRow,
    spritesPerColumn,
    spriteWidth,
    spriteHeight,
    sheetURLs
  } = thumbs;

  const thumbsPerSheet = spritesPerRow * spritesPerColumn;
  const totalSheets = sheetURLs.length;

  if (duration <= 0 || totalSpriteCount === 0) return null;

  const index = Math.floor((position / duration) * totalSpriteCount);
  if (index < 0 || index >= totalSpriteCount) return null;

  const sheetIndex = Math.min(
    Math.floor(index / thumbsPerSheet),
    totalSheets - 1
  );
  const indexInSheet = index % thumbsPerSheet;

  const row = Math.floor(indexInSheet / spritesPerRow);
  const col = indexInSheet % spritesPerRow;

  const x = col * spriteWidth;
  const y = row * spriteHeight;

  return {
    sheetURL: sheetURLs[sheetIndex],
    x,
    y,
    width: spriteWidth,
    height: spriteHeight,
    index,
    sheetIndex
  };
}

/**
 * Loads (and caches) a thumbnail sheet image, then returns a cropped canvas.
 * Will only load each sprite sheet once.
 *
 * @param thumbnail Thumbnail metadata (from getThumbnailForPosition)
 * @returns Observable<HTMLCanvasElement>
 */
export function getThumbnailImage$(thumbnail: IThumbnailResult): Observable<HTMLCanvasElement> {
  const { sheetURL, x, y, width, height } = thumbnail;

  // If cached → return observable of the cached image
  const image$ = imageCache.has(sheetURL)
    ? of(imageCache.get(sheetURL)!)
    : loadImage$(sheetURL).pipe(
      map(img => {
        imageCache.set(sheetURL, img);
        return img;
      })
    );

  return image$.pipe(
    map(img => {
      // Create canvas
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to get canvas context");

      // Draw cropped sprite
      ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

      return canvas;
    })
  );
}

function loadImage$(url: string): Observable<HTMLImageElement> {
  return new Observable(observer => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;

    img.onload = () => {
      observer.next(img);
      observer.complete();
    };

    img.onerror = () => {
      observer.error(new Error(`Failed to load sprite sheet: ${url}`));
    };
  });
}
